import { PartnerHoliday } from '@provision/entity-types';
import { useMutation } from '@provision/graphql-client';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { partition } from 'remeda';
import { DATE_TIME_FORMAT } from 'src/constants/date';
import {
  PartnerHolidayEntity,
  PartnerHolidayMutationResponse,
  PartnerHolidayQueryResponse,
} from 'src/graphql/entities/partner-holiday/partner-holiday.types';
import {
  CREATE_PARTNER_HOLIDAY_QUERY,
  GET_PARTNER_HOLIDAY_QUERY,
} from 'src/graphql/entities/partner-holiday/partner-holidays.operations';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { QueryVariables } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';
import { GetPartnerHolidayQuery } from './use-partner-holiday';

export interface CreatePartnerHolidayMutation {
  createPartnerHoliday: PartnerHolidayMutationResponse[];
}

export type CreatePartnerHolidayMutationParams = {
  data: Pick<
    PartnerHoliday,
    'holidayName' | 'partnerId' | 'holidayStart' | 'holidayEnd' | 'submitterId'
  >;
};

export const useCreatePartnerHoliday = () => {
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const { user } = useCurrentUserProfile();
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const today = useMemo(() => dayjs().startOf('day').format(DATE_TIME_FORMAT), []);

  const [createPartnerHolidayMutation, { loading: isCreating }] = useMutation<
    CreatePartnerHolidayMutation,
    CreatePartnerHolidayMutationParams
  >(CREATE_PARTNER_HOLIDAY_QUERY, {
    update(cache, { data }) {
      if (!data?.createPartnerHoliday?.length) {
        return;
      }

      const [upcomingHolidays, pastHolidays] = partition(data.createPartnerHoliday, (value) =>
        dayjs(value.holidayEnd).isSameOrAfter(today),
      );

      if (upcomingHolidays.length) {
        cache.updateQuery<GetPartnerHolidayQuery, QueryVariables>(
          {
            query: GET_PARTNER_HOLIDAY_QUERY,
            variables: {
              params: {
                filters: [
                  {
                    attribute: 'partnerId',
                    operator: '=',
                    value: partnerId?.toString()!,
                  },
                  {
                    attribute: 'holidayEnd',
                    operator: '>=',
                    value: today,
                  },
                ],
              },
            },
          },
          updateCacheFn(upcomingHolidays, user),
        );
      }
      if (pastHolidays.length) {
        cache.updateQuery<GetPartnerHolidayQuery, QueryVariables>(
          {
            query: GET_PARTNER_HOLIDAY_QUERY,
            variables: {
              params: {
                filters: [
                  {
                    attribute: 'partnerId',
                    operator: '=',
                    value: partnerId?.toString()!,
                  },
                  {
                    attribute: 'holidayEnd',
                    operator: '<',
                    value: today,
                  },
                ],
              },
            },
          },
          updateCacheFn(pastHolidays, user),
        );
      }
    },
    optimisticResponse: (params) => ({
      createPartnerHoliday: [
        {
          __typename: 'PartnerHoliday',
          id: new Date().getTime().toString(),
          partnerHolidayId: new Date().getTime().toString(),
          partnerId: params.data.partnerId,
          holidayName: params.data.holidayName,
          holidayStart: params.data.holidayStart,
          holidayEnd: params.data.holidayEnd,
          submitterId: params.data.submitterId,
          createdAt: new Date().toLocaleTimeString(),
          updatedAt: new Date().toLocaleTimeString(),
          platformUser: {
            id: user?.id ?? 'userId',
            userId: user?.userId ?? 'userId',
            firstName: user?.firstName ?? '',
            lastName: user?.lastName ?? '',
          },
        },
      ],
    }),
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to create holiday entity, please try again');
    },
    onCompleted: (data) => {
      showSuccessAlert(
        `${data.createPartnerHoliday?.[0].holidayName ?? 'New'} holiday created!`,
        { autoHide: 2000 },
      );
    },
  });

  const createPartnerHoliday = useCallback(
    async (
      value: Pick<PartnerHolidayEntity, 'holidayStart' | 'holidayEnd' | 'holidayName'>,
    ) => {
      if (!user?.userId) {
        console.error('Cannot create holiday entity. Current user is not found');
        return;
      }
      if (partnerId === null) {
        console.error('Cannot create holiday entity. Current partner Id is not found');
        return;
      }
      await createPartnerHolidayMutation({
        variables: {
          data: {
            submitterId: user.userId,
            partnerId,
            holidayName: value.holidayName,
            holidayStart: value.holidayStart.format(DATE_TIME_FORMAT),
            holidayEnd: value.holidayEnd.format(DATE_TIME_FORMAT),
          },
        },
      });
    },
    [createPartnerHolidayMutation, partnerId, user?.userId],
  );

  return {
    createPartnerHoliday,
    isCreating,
  };
};

function updateCacheFn(
  newRecords: PartnerHolidayMutationResponse[],
  user: PartnerHolidayQueryResponse['platformUser'] | null,
) {
  return (cached: GetPartnerHolidayQuery | null) => {
    const records = (newRecords || []).map<PartnerHolidayQueryResponse>((record) => ({
      ...record,
      platformUser: {
        id: user?.id || '',
        userId: user?.userId || '',
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
      },
    }));

    return {
      getPartnerHoliday: {
        __typename: 'PartnerHolidayDataResponse',
        records: [...(cached?.getPartnerHoliday?.records || []), ...records],
        metadata: cached?.getPartnerHoliday.metadata ?? {},
      },
    };
  };
}
